import React from 'react';
import {StyleProp, StyleSheet, ViewStyle} from 'react-native';
import CCButton from '@/Atoms/CCButton';

// Profile-screen button, with the design handoff's exact CTA tokens baked in
// (DEV_HANDOFF_SPEC §2 "CTA buttons") so no screen has to re-fight CCButton's
// defaults. Two quirks this wrapper neutralises centrally:
//   1. CCButton's fill variant renders WHITE label text -- the design's yellow
//      primary uses brown text, so we always set titleStyle color.
//   2. CCButton derives `borderBottomColor` from titleStyle.color, which would
//      paint a dark bottom edge on the light-grey outline -- we pin
//      borderBottomColor back to the border color on the secondary variant.
// Change a token here once and every profile button updates.
// 'dark' is the design's brown CTA used where a gold primary would disappear
// into a yellow surface (e.g. the Assessment banner) -- brown fill, white label.
type Variant = 'primary' | 'secondary' | 'dark';

type Props = {
  variant?: Variant;
  onPress?: () => void;
  disabled?: boolean;
  testID?: string;
  style?: StyleProp<ViewStyle>;
  children: React.ReactNode;
};

export default function ProfileButton({
  variant = 'primary',
  onPress,
  disabled,
  testID,
  style,
  children,
}: Props) {
  const isPrimary = variant === 'primary';
  const isDark = variant === 'dark';
  const isFill = isPrimary || isDark;
  return (
    <CCButton
      type={isFill ? 'fill' : 'outline'}
      // Spec §5 button hover: lift translateY(-2px) on hover (CCButton's
      // `animated` mode provides exactly this on web).
      animated
      onPress={onPress}
      disabled={disabled}
      testID={testID}
      // DM Sans SemiBold (600) -- the design's button weight. This is a real
      // master weight, not a faux-bold, so it renders at the intended weight.
      titleFontWeight="semibold"
      titleStyle={
        isDark
          ? styles.darkText
          : isPrimary
          ? styles.primaryText
          : styles.secondaryText
      }
      style={[
        isDark ? styles.dark : isPrimary ? styles.primary : styles.secondary,
        style,
      ]}
      contentContainerStyle={styles.content}>
      {children}
    </CCButton>
  );
}

const styles = StyleSheet.create({
  content: {
    paddingVertical: 13,
    paddingHorizontal: 22,
  },
  primary: {
    backgroundColor: '#FACF47',
  },
  primaryText: {
    color: '#4D3F37',
  },
  dark: {
    backgroundColor: '#4D3F37',
  },
  darkText: {
    color: '#fff',
  },
  secondary: {
    backgroundColor: '#fff',
    borderColor: 'rgba(38,35,34,0.2)',
    // Explicit -- overrides CCButton's title-color-derived bottom border.
    borderBottomColor: 'rgba(38,35,34,0.2)',
    borderWidth: 1,
    borderStyle: 'solid',
  },
  secondaryText: {
    color: '#4D3F37',
  },
});
